import { FRONTEND_URL } from "../config";
import { setAuthorizationHeader } from "./apiHelper";
import { UserAuthData, UserLoginResponse } from "./backendResponseInterfaces";

interface MonthCodes {
  [key: string]: string;
}

const monthCodes: MonthCodes = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  O: "Oct",
  N: "Nov",
  D: "Dec",
};

export const getFormattedStrike = (input: string, index: string): string => {
  const idx = index.replace(/\d+/g, "");
  const indexLen = idx.length;
  const year = input.slice(indexLen, indexLen + 2);
  let month = input.slice(indexLen + 2, indexLen + 3);
  let date = input.slice(indexLen + 3, indexLen + 5);
  const strikePrice = input.slice(indexLen + 5, -2);

  if (month in monthCodes) {
    if (isNaN(parseInt(month))) {
      month = monthCodes[month];
    } else {
      month = monthCodes[month];
    }

    if (isNaN(parseInt(date))) {
      date = month;
    }
  } else {
    date = month + date;
    month = "";
  }

  return `${strikePrice} '${year} ${date} ${month}`;
};

export const formatStrikeCoiNt = (
  instrumentName: string,
  index: string,
): string => instrumentName.slice(index.replace(/\d+/g, "").length + 5);

export const displayTimeFormat_hh_mm_ss = (
  date: Date | string,
  showSecond: boolean = false,
): string => {
  const dateFmt: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };
  if (showSecond) dateFmt.second = "2-digit";
  return new Date(date).toLocaleTimeString("en-US", dateFmt);
};

export const capitalizeFirstLetter = (string: string): string =>
  string.charAt(0).toUpperCase() + string.slice(1);

export const getFutFormattedString = (futSymbol: string): string =>
  `${futSymbol.substring(
    futSymbol.length - 6,
    futSymbol.length - 3,
  )} '${futSymbol.substring(futSymbol.length - 8, futSymbol.length - 6)}`;

export const getFormattedOptionsFromArr = (
  arr: any[],
): { value: any; label: any }[] => arr.map((a) => ({ value: a, label: a }));

export const getFormattedtFutName = (futName: string): string =>
  futName.slice(-6, -3) + " '" + futName.slice(-8, -6);

export const getArrAvg = (arr: number[]): number =>
  arr.reduce((a, b) => a + b, 0) / arr.length;

export const userLogin = (data: { data: UserLoginResponse }): void => {
  localStorage.setItem("userData", JSON.stringify(data.data));
  setAuthorizationHeader(data.data.token);
};

export const userLogout = (): void => {
  localStorage.removeItem("userData");
  setAuthorizationHeader(null);
};

export const getUserData = (
  firstCall: boolean = false,
): UserAuthData | null => {
  const data = localStorage.getItem("userData");
  if (!data) return null;
  const userData = JSON.parse(data);
  if (firstCall) setAuthorizationHeader(userData.token);
  return userData;
};

export const getNameInitials = (name: string): string =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

export const defaultEncodeID = (id: number, idType: string): string => {
  const data = JSON.stringify({ idType, id });
  return btoa(data);
};

export const defaultDecodeID = (encodedStr: string, idType: string): any => {
  const decodedStr = atob(encodedStr);
  const decodedData = JSON.parse(decodedStr);
  if (decodedData.idType === idType) {
    return decodedData.id;
  } else {
    return null;
  }
};

export const getFEURL = (): string | undefined => FRONTEND_URL;

export const formatCreatedAt = (createdAt: string, sinceTime?: string) => {
  const date = new Date(createdAt);
  const now = new Date();

  const isSameDay = date.toDateString() === now.toDateString();
  const isYesterday =
    new Date(now.setDate(now.getDate() - 1)).toDateString() ===
    date.toDateString();

  if (isSameDay) {
    return new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  } else if (isYesterday) {
    return `${new Intl.DateTimeFormat(undefined, {
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date)} yesterday`;
  }

  if (sinceTime) {
    return sinceTime;
  } else {
    return new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    }).format(date);
  }
};
